import React, { useRef } from "react";
import { Dropdown, Menu, Modal } from "antd";
import { FileExcelOutlined, DownOutlined, UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import { ButtonItem } from "../Items";
import { aplicarLotesAlForm } from "../LeerPdf/aplicarLotesAlForm";
import {
  construirPlantilla,
  entradasSinEmpatar,
  leerFilasDeArchivo,
  parsearHojaLotes,
} from "./lotesExcel";

const MAX_LISTADO = 30;

const listaRecortada = (titulo, elementos) =>
  elementos.length === 0 ? null : (
    <div style={{ marginTop: 12 }}>
      <strong>{titulo}</strong>
      <ul style={{ maxHeight: 220, overflowY: "auto", paddingLeft: 18, marginBottom: 0 }}>
        {elementos.slice(0, MAX_LISTADO).map((texto, i) => (
          <li key={i}>{texto}</li>
        ))}
        {elementos.length > MAX_LISTADO ? <li>… y {elementos.length - MAX_LISTADO} más</li> : null}
      </ul>
    </div>
  );

/**
 * Carga lotes/caducidades/cantidad a recibir desde un Excel, hermano de LeerPdf: misma firma,
 * mismo empate contra los renglones del form (no crea renglones).
 *
 * Props:
 *   formRef         ref      Form de antd con el Form.List `insumos`
 *   todasInsumos    array    catálogo de la pantalla (insumos y relaciones)
 *   onLoadingChange fn       (boolean) para el Spin del padre
 *   titulo          string   opcional, encabezado y nombre de archivo de la plantilla
 */
function LeerExcelLotes({ formRef, todasInsumos, onLoadingChange, titulo }) {
  const inputRef = useRef(null);

  const descargarPlantilla = () => {
    const insumos = formRef.current?.getFieldValue("insumos") || [];
    const { libro, renglones } = construirPlantilla(insumos, todasInsumos, titulo);
    if (renglones === 0) {
      Modal.info({ title: "No hay insumos pendientes de recibir para armar la plantilla.", centered: true });
      return;
    }
    const nombre = (titulo || "Plantilla de lotes").replace(/[\\/:*?"<>|]/g, "-");
    XLSXStyle.writeFile(libro, `${nombre}.xlsx`);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";

    onLoadingChange?.(true);
    try {
      const filas = leerFilasDeArchivo(await file.arrayBuffer());
      const { mapa, errores, renglones } = parsearHojaLotes(filas);

      if (!mapa) {
        Modal.warning({
          title: "No se reconocieron las columnas del Excel",
          content:
            "Se necesita un renglón de encabezados con Clave/Código o Descripción, y Lote o Cantidad (Caducidad es opcional). Descarga la plantilla para llenarla con el formato correcto.",
          centered: true,
        });
      } else if (renglones === 0) {
        Modal.warning({
          title: "El Excel no trae lotes ni cantidades capturadas",
          content: listaRecortada("Observaciones:", errores),
          centered: true,
        });
      } else {
        const { matched, total, entradasUsadas } = aplicarLotesAlForm(formRef, todasInsumos, mapa);
        const sinEmpatar = entradasSinEmpatar(mapa, entradasUsadas);
        const contenido = (
          <div>
            {matched > 0 ? <div>Favor de verificar.</div> : null}
            {listaRecortada("Productos del Excel que no están en el listado:", sinEmpatar)}
            {listaRecortada("Observaciones:", errores)}
          </div>
        );
        const hayAvisos = sinEmpatar.length > 0 || errores.length > 0;
        if (matched === 0) {
          Modal.warning({
            title: "Ningún producto del Excel coincidió con los insumos del listado",
            content: contenido,
            centered: true,
            width: 600,
          });
        } else {
          (hayAvisos ? Modal.warning : Modal.success)({
            title: `Se cargaron lotes para ${matched} insumo(s) de ${total} en el listado.`,
            content: contenido,
            centered: true,
            width: hayAvisos ? 600 : undefined,
          });
        }
      }
    } catch (err) {
      console.error("Error al leer Excel de lotes:", err);
      Modal.error({ title: "Error al leer el Excel", centered: true });
    }
    onLoadingChange?.(false);
  };

  const menu = (
    <Menu
      onClick={({ key }) => {
        if (key === "cargar") inputRef.current?.click();
        if (key === "plantilla") descargarPlantilla();
      }}
    >
      <Menu.Item key="cargar" icon={<UploadOutlined />}>
        Cargar lotes desde Excel
      </Menu.Item>
      <Menu.Item key="plantilla" icon={<DownloadOutlined />}>
        Descargar plantilla
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      {/* span: ButtonItem no reenvía ref, y el Dropdown necesita un nodo DOM al cual anclarse */}
      <Dropdown overlay={menu} trigger={["click"]}>
        <span style={{ display: "inline-block" }}>
          <ButtonItem icon={<FileExcelOutlined />} lessTime>
            Lotes Excel <DownOutlined />
          </ButtonItem>
        </span>
      </Dropdown>
      <input
        type="file"
        ref={inputRef}
        accept=".xlsx,.xls,.csv"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </>
  );
}

export default LeerExcelLotes;
